import React, { FC, useState, useEffect } from 'react'
import { I18n, Auth } from 'aws-amplify'
import {
  makeStyles,
  Theme,
  Grid,
  TextField,
  Link,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core'

import { PillButton, PasswordRequirementsTooltip } from 'components'
import { AuthFlowSteps, EventStages, IUser, IOption } from 'types'
import { graphQLQuery, graphQLMutation } from 'graphql/helpers'
import { updateUser } from 'graphql/mutations'
import { userByEmail, listSessions } from 'graphql/queries'
import { useHistory } from 'react-router-dom'

interface CommunicationPrefProps {
  userEmail: string
  setAuthState: (state: AuthFlowSteps) => void
  eventStage?: EventStages
  setUser: (payload: IUser) => void
}

export const CommunicationPref: FC<CommunicationPrefProps> = ({ setAuthState, userEmail, setUser, eventStage }) => {
  const classes = useStyles()
  const history = useHistory()
  const [loading, setLoading] = useState<boolean>(false)
  const [userCommPreference, setUserCommPreference] = useState<boolean>(false)

  useEffect(() => {
    // back to no -- just to be safe
    return () => setUserCommPreference(false)
  }, [])

  const getCurrentUser = async () => {
    const foundUser = await graphQLQuery(userByEmail, 'userByEmail', { email: userEmail })
    if (!foundUser) {
      setAuthState(AuthFlowSteps.Register)
    }
    return foundUser
  }

  const saveUserCommPreference = async () => {
    setLoading(true)
    try {
      const userRes = await getCurrentUser()
      await graphQLMutation(updateUser, {
        id: userRes.id,
        allowVerizonComm: userCommPreference
      })
      setUser(userRes)
      if (eventStage !== EventStages.REGISTRATION) {
        history.push('/event')
      } else {
        setAuthState(AuthFlowSteps.ThankYou)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container direction='column' justify='center' spacing={2}>
      <Grid item>
        <Typography variant='h2' className={classes.heading} paragraph>
          <span dangerouslySetInnerHTML={{ __html: I18n.get('commPreferenceTitle') }}></span>
        </Typography>
      </Grid>
      <Grid item container alignItems='center'>
        <Typography component='p' paragraph>
          <span dangerouslySetInnerHTML={{ __html: I18n.get('commPreferenceBlurb') }}></span>
        </Typography>
      </Grid>
      <Grid item container spacing={2} justify='center'>
        <Grid item xs={12}>
          <FormControl fullWidth variant='outlined' className={classes.input}>
            <InputLabel id='commPrefLabel'>{I18n.get('commPreferenceLabel')}</InputLabel>
            <Select
              labelId='commPrefLabel'
              name='select-communication-preference'
              fullWidth
              value={userCommPreference}
              label={I18n.get('commPreferenceLabel')}
              onChange={(e: React.ChangeEvent<{ value: unknown }>) => setUserCommPreference(e.target.value === 'true')}
            >
              <MenuItem value='false'>{I18n.get(`commPreferenceNo`)}</MenuItem>
              <MenuItem value='true'>{I18n.get(`commPreferenceYes`)}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container item xs={12}>
        <PillButton
          loading={loading}
          type='submit'
          onClick={() => saveUserCommPreference()}
          solid
          className={classes.button}
        >
          {I18n.get('finish')}
        </PillButton>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontWeight: 700,
    fontSize: '3.125rem',
    lineHeight: '3.125rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '3rem',
      lineHeight: '3rem'
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '2rem'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '2rem',
      lineHeight: '2rem'
    }
  },
  button: {
    width: 165,
    marginRight: '20px',
    marginBottom: '20px',
    marginTop: '1rem'
  },
  input: {
    color: '#000',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& label': {
      color: theme.palette.grey[500]
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  inlineButton: {
    color: '#000',
    margin: '0 .5rem',
    fontFamily: 'Verizon-Regular',
    textDecoration: 'underline',
    verticalAlign: 'initial'
  },
  arrow: {
    color: '#000'
  },
  tooltip: {
    backgroundColor: '#fff',
    border: '1px solid #000'
  },
  tooltipContainer: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column'
  },
  tooltipItem: {
    color: '#000',
    display: 'flex',
    alignItems: 'center'
  },

  tooltipIcon: {
    color: theme.palette.grey[500],
    '&:hover': {
      cursor: 'pointer'
    }
  }
}))
